import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Settings, Plus, X, Trash2 } from "lucide-react";
import { AppConfigKey } from "@/types/application";
import { AppConfigKeyEditor } from "@/components/applications/config/AppConfigKeyEditor";

export interface InitialConfiguration {
  name: string;
  description: string;
  keys: AppConfigKey[];
}

interface AppConfigurationStepProps {
  configuration: InitialConfiguration | null;
  onConfigurationChange: (config: InitialConfiguration | null) => void;
}

export const AppConfigurationStep = ({
  configuration,
  onConfigurationChange,
}: AppConfigurationStepProps) => {
  const [isAdding, setIsAdding] = useState(false);
  const [configName, setConfigName] = useState(configuration?.name || "");
  const [configDescription, setConfigDescription] = useState(configuration?.description || "");
  const [configKeys, setConfigKeys] = useState<AppConfigKey[]>(configuration?.keys || []);

  const handleAddConfiguration = () => {
    setIsAdding(true);
    setConfigName("Default Configuration");
    setConfigDescription("");
    setConfigKeys([]);
  };

  const handleSaveConfiguration = () => {
    if (!configName.trim()) return;
    
    onConfigurationChange({
      name: configName,
      description: configDescription,
      keys: configKeys,
    });
    setIsAdding(false);
  };

  const handleRemoveConfiguration = () => {
    onConfigurationChange(null);
    setIsAdding(false);
    setConfigName("");
    setConfigDescription("");
    setConfigKeys([]);
  };

  const handleAddKey = () => {
    const newKey: AppConfigKey = {
      key: "",
      value: "",
      type: "string",
    };
    setConfigKeys([...configKeys, newKey]);
  };

  const handleKeyChange = (index: number, updatedKey: AppConfigKey) => {
    const newKeys = [...configKeys];
    newKeys[index] = updatedKey;
    setConfigKeys(newKeys);
  };

  const handleRemoveKey = (index: number) => {
    setConfigKeys(configKeys.filter((_, i) => i !== index));
  };

  // Show saved configuration summary
  if (configuration && !isAdding) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">App Configuration (Optional)</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Initial configuration has been added. You can modify or remove it.
          </p>
        </div>

        <Card className="border-primary bg-primary/5">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-base">{configuration.name}</CardTitle>
                {configuration.description && (
                  <CardDescription className="mt-1">{configuration.description}</CardDescription>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setConfigName(configuration.name);
                    setConfigDescription(configuration.description);
                    setConfigKeys(configuration.keys);
                    setIsAdding(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveConfiguration}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-sm text-muted-foreground">
              {configuration.keys.length} configuration key{configuration.keys.length !== 1 ? "s" : ""} defined
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show add configuration form
  if (isAdding) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">App Configuration (Optional)</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Add configuration settings for this application.
          </p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Configuration Details</CardTitle>
              <Button variant="ghost" size="sm" onClick={handleRemoveConfiguration}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="config-name">Configuration Name *</Label>
              <Input
                id="config-name"
                value={configName}
                onChange={(e) => setConfigName(e.target.value)}
                placeholder="e.g., Default Configuration"
              />
            </div>

            <div>
              <Label htmlFor="config-description">Description</Label>
              <Textarea
                id="config-description"
                value={configDescription}
                onChange={(e) => setConfigDescription(e.target.value)}
                placeholder="Optional description for this configuration"
                rows={2}
              />
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label>Configuration Keys</Label>
                <Button variant="outline" size="sm" onClick={handleAddKey}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Key
                </Button>
              </div>

              {configKeys.length === 0 ? (
                <div className="text-center py-6 border rounded-lg border-dashed">
                  <p className="text-sm text-muted-foreground">
                    No configuration keys added yet.
                  </p>
                  <Button variant="link" size="sm" onClick={handleAddKey} className="mt-1">
                    Add your first key
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {configKeys.map((key, index) => (
                    <AppConfigKeyEditor
                      key={index}
                      configKey={key}
                      onChange={(updatedKey) => handleKeyChange(index, updatedKey)}
                      onRemove={() => handleRemoveKey(index)}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={handleRemoveConfiguration}>
                Cancel
              </Button>
              <Button onClick={handleSaveConfiguration} disabled={!configName.trim()}>
                Save Configuration
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show initial state - option to add configuration
  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Settings className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">App Configuration (Optional)</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Optionally add initial configuration settings for this application. 
          You can also add configurations later from the application detail page.
        </p>
      </div>

      <Card className="border-dashed">
        <CardContent className="py-12 text-center">
          <Settings className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-medium mb-2">No Configuration Added</h3>
          <p className="text-sm text-muted-foreground mb-4">
            You can add an initial app configuration with key-value settings.
          </p>
          <Button onClick={handleAddConfiguration}>
            <Plus className="h-4 w-4 mr-2" />
            Add Configuration
          </Button>
        </CardContent>
      </Card>

      <p className="text-sm text-muted-foreground text-center mt-4">
        This step is optional — you can skip and add configurations later.
      </p>
    </div>
  );
};
